import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { MapPin, Activity, Clock, User, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon with count
const createCustomIcon = (count) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative;">
        <div style="
          width: 40px;
          height: 40px;
          background: #10b981;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 4px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          font-size: 14px;
          cursor: pointer;
          animation: pulse 2s infinite;
        ">
          ${count}
        </div>
      </div>
      <style>
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
      </style>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

// World map with major regions and US states
const REGION_COORDINATES = {
  // North America - Countries
  'United States': { lat: 37.0902, lng: -95.7129 },
  'Canada': { lat: 56.1304, lng: -106.3468 },
  'Mexico': { lat: 23.6345, lng: -102.5528 },
  
  // US States
  'Alabama': { lat: 32.3182, lng: -86.9023 },
  'Alaska': { lat: 61.3707, lng: -152.4044 },
  'Arizona': { lat: 33.7298, lng: -111.4312 },
  'California': { lat: 36.1162, lng: -119.6816 },
  'Colorado': { lat: 39.0598, lng: -105.3111 },
  'Florida': { lat: 27.7663, lng: -81.6868 },
  'Georgia': { lat: 33.0406, lng: -83.6431 },
  'Illinois': { lat: 40.3495, lng: -88.9861 },
  'Iowa': { lat: 42.0115, lng: -93.2105 },
  'New York': { lat: 42.1657, lng: -74.9481 },
  'Texas': { lat: 31.0545, lng: -97.5635 },
  'Washington': { lat: 47.4009, lng: -121.4905 },
  
  // Europe
  'United Kingdom': { lat: 55.3781, lng: -3.4360 },
  'Germany': { lat: 51.1657, lng: 10.4515 },
  'France': { lat: 46.2276, lng: 2.2137 },
  'Spain': { lat: 40.4637, lng: -3.7492 },
  'Italy': { lat: 41.8719, lng: 12.5674 },
  
  // Asia
  'China': { lat: 35.8617, lng: 104.1954 },
  'Japan': { lat: 36.2048, lng: 138.2529 },
  'India': { lat: 20.5937, lng: 78.9629 },
  'Singapore': { lat: 1.3521, lng: 103.8198 },
  
  // Default fallback
  'default': { lat: 20, lng: 0 }
};

const getCoordinatesFromRegion = (region) => {
  // Extract parts from region string (e.g., "Council Bluffs, Iowa, United States")
  const parts = region.split(',').map(p => p.trim());
  
  // For US locations, try to get state (second to last part)
  if (parts.length >= 3 && parts[parts.length - 1] === 'United States') {
    const state = parts[parts.length - 2];
    if (REGION_COORDINATES[state]) {
      return REGION_COORDINATES[state];
    }
  }
  
  // Otherwise, use country (last part)
  const country = parts[parts.length - 1];
  return REGION_COORDINATES[country] || REGION_COORDINATES['default'];
};

// Map Controls Component
const MapControls = () => {
  const map = useMap();

  const handleZoomIn = () => {
    map.zoomIn();
  };

  const handleZoomOut = () => {
    map.zoomOut();
  };

  const handleResetView = () => {
    map.setView([20, 0], 2);
  };

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
      <button
        onClick={handleZoomIn}
        className="bg-white hover:bg-slate-100 text-slate-700 p-2 rounded-lg shadow-lg border border-slate-200"
        title="Zoom In"
      >
        <ZoomIn className="h-5 w-5" />
      </button>
      <button
        onClick={handleZoomOut}
        className="bg-white hover:bg-slate-100 text-slate-700 p-2 rounded-lg shadow-lg border border-slate-200"
        title="Zoom Out"
      >
        <ZoomOut className="h-5 w-5" />
      </button>
      <button
        onClick={handleResetView}
        className="bg-white hover:bg-slate-100 text-slate-700 p-2 rounded-lg shadow-lg border border-slate-200"
        title="Reset View"
      >
        <Maximize2 className="h-5 w-5" />
      </button>
    </div>
  );
};

export const LoginLocationsMap = ({ adminToken }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (adminToken) {
      fetchLocations();
    }
  }, [adminToken]);

  const fetchLocations = async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/logs/locations`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLocations(response.data.locations);
    } catch (error) {
      console.error('Error fetching login locations:', error);
      toast.error('Failed to load login locations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Global Login Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Global Login Activity
          </CardTitle>
          <CardDescription className="font-ibm">
            Geographic distribution of login attempts ({locations.length} locations) - Zoom and pan to explore
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative h-[500px] rounded-lg overflow-hidden border border-slate-200 z-0">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              minZoom={2}
              maxZoom={18}
              style={{ height: '100%', width: '100%', zIndex: 0 }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {locations.map((location, index) => {
                const coords = getCoordinatesFromRegion(location.region);
                return (
                  <Marker
                    key={index}
                    position={[coords.lat, coords.lng]}
                    icon={createCustomIcon(location.count)}
                    eventHandlers={{
                      click: () => {
                        setSelectedLocation(location);
                      },
                    }}
                  >
                    <Popup>
                      <div className="font-ibm">
                        <div className="font-semibold text-base mb-2">{location.region}</div>
                        <div className="text-sm text-slate-600">
                          {location.count} login attempt(s)
                        </div>
                        <button
                          onClick={() => setSelectedLocation(location)}
                          className="text-emerald hover:text-emerald/80 text-sm font-medium mt-2"
                        >
                          View Details →
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              <MapControls />
            </MapContainer>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2 text-xs font-ibm mb-1">
                <div className="w-4 h-4 bg-emerald rounded-full"></div>
                <span className="font-medium">Login Location</span>
              </div>
              <div className="text-xs text-slate-600 font-ibm">
                Click markers for details
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location Details Dialog */}
      <Dialog open={!!selectedLocation} onOpenChange={() => setSelectedLocation(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald" />
              {selectedLocation?.region}
            </DialogTitle>
            <DialogDescription className="font-ibm">
              {selectedLocation?.count} login attempt(s) from this location
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {selectedLocation?.logins.map((login, index) => (
              <div key={index} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Activity className={`h-4 w-4 ${login.status === 'success' ? 'text-green-600' : 'text-red-600'}`} />
                    <span className={`font-ibm font-medium text-sm ${login.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                      {login.action === 'login' ? 'Login Success' : 'Login Attempt'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-600 font-ibm">
                    <Clock className="h-3 w-3" />
                    {formatDate(login.timestamp)}
                  </div>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-slate-700 font-ibm">
                    <User className="h-3 w-3" />
                    <span className="font-medium">{login.admin_username}</span>
                  </div>
                  <div className="text-xs text-slate-600 font-ibm">
                    IP: {login.ip_address}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
